import { useEffect, useMemo, useRef, useState } from 'react'
import { Point } from 'mapbox-gl'

import { InjectionTokens } from '@/controller/tokens'

import { UserWaypointChanged } from '@/domain/events/UserWaypointChanged'
import { ADHP, Waypoint } from '@/domain/models'
import { MapPosition } from '@/domain/protocols/MapPosition'
import {
  UserWaypointVisibilityState,
  UserWaypointVisibilityStateProps
} from '@/domain/states/UserWaypointVisibilityState'

import { IAeroInfoRepository } from '@/data/AeroInfoRepository/IAeroInfoRepository'
import { IUserWaypointsRepository } from '@/data/UserWaypointsRepository/IUserWaypointsRepository'

import { MapADHP } from '../components/MapADHP/ADHPs/MapADHP'
import { WaypointInfoContainer } from '../components/MapADHP/WaypointInfo/WaypointInfoContainer'
import { ignoreMapClickLayers, isAirspaceInteractiveLayer } from '../components/MapView/constants'
import { useMapContext } from '../components/MapView/MapContext'
import { MapImperativeInterface } from '../interfaces/MapImperativeInterface'
import { MapADHPState, MapADHPStateProps } from '../states/MapADHPState'
import { MeasuringSegmentState } from '../states/MapMeasuringSegmentState'
import { getFeatureFlyToPadding } from '../states/ScreenElementsState'

import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'

function MapADHPPresenter() {
  const aeroinfoRepository = useInjection<IAeroInfoRepository>(InjectionTokens.AeroInfoRepository)
  const userWaypointsRepository = useInjection<IUserWaypointsRepository>(InjectionTokens.UserWaypointsRepository)
  const mapADHPState = useBehaviorSubject<MapADHPStateProps>(MapADHPState)
  const measuringSegmentState = useBehaviorSubject(MeasuringSegmentState)
  const [waypointVisibility] = useGlobalState<UserWaypointVisibilityState, UserWaypointVisibilityStateProps>(
    InjectionTokens.UserWaypointVisibilityState
  )

  const mapContext = useMapContext()

  const [_adhps, setAdhps] = useState<ADHP[]>([])
  const [_userWaypoints, setUserWaypoints] = useState<Waypoint[]>([])
  const [focusedWaypoint, setFocusedWaypoint] = useState<string | null>(null)
  const [waypoint, setWaypoint] = useState<Waypoint | null>(null)
  const [waypointInfoOnMapPos, setWaypointInfoOnMapPos] = useState<MapPosition['position']>({ x: 0, y: 0 })
  const balloonTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [menuPosition, setMenuPosition] = useState<
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right'
    | 'center-left'
    | 'center-right'
    | 'center-bottom'
    | 'center-top'
    | 'default'
  >('top-left')

  useEffect(() => {
    const fetchADHPs = () => {
      const adhpsRequest = aeroinfoRepository.getAllADHPs()

      if (adhpsRequest.isSuccess) setAdhps(adhpsRequest.getValue())
    }

    const fetchUserWaypoints = () => {
      const userWaypointsRequest = userWaypointsRepository.getWaypoints()

      if (userWaypointsRequest.isSuccess) setUserWaypoints(userWaypointsRequest.getValue())
    }

    fetchADHPs()
    fetchUserWaypoints()

    // Sem esta assinatura o mapa leria os pontos do usuário só no mount: criar, editar,
    // importar ou remover um ponto não apareceria (nem seria clicável) até um reload.
    const subscription = UserWaypointChanged.subscribe(() => fetchUserWaypoints())
    return () => subscription.unsubscribe()
  }, [aeroinfoRepository, userWaypointsRepository])

  useEffect(() => () => clearTimeout(balloonTimeoutRef.current ?? undefined), [])

  const adhps = useMemo(() => [..._adhps, ..._userWaypoints], [_adhps, _userWaypoints])

  const waypointInfoMenuPosition = useMemo(() => {
    const { innerWidth: width, innerHeight: height } = window

    const margin = 200
    const menuWidth = 300

    const screenRightLimit = width - margin - menuWidth
    const screenLeftLimit = margin
    const screenBottomLimit = height - margin
    const screenTopLimit = margin

    if (waypointInfoOnMapPos.y > screenBottomLimit) {
      if (waypointInfoOnMapPos.x < screenLeftLimit) {
        setMenuPosition('bottom-left')

        return {
          top: waypointInfoOnMapPos.y,
          left: waypointInfoOnMapPos.x,
          translateX: '10%',
          translateY: '-110%'
        }
      }

      if (waypointInfoOnMapPos.x > screenRightLimit) {
        setMenuPosition('bottom-right')

        return {
          top: waypointInfoOnMapPos.y,
          left: waypointInfoOnMapPos.x,
          translateX: '-110%',
          translateY: '-110%'
        }
      }

      setMenuPosition('center-bottom')

      return {
        top: waypointInfoOnMapPos.y,
        left: waypointInfoOnMapPos.x,
        translateX: '-50%',
        translateY: '-110%'
      }
    }

    if (waypointInfoOnMapPos.y < screenTopLimit) {
      if (waypointInfoOnMapPos.x < screenLeftLimit) {
        setMenuPosition('top-left')

        return {
          top: waypointInfoOnMapPos.y,
          left: waypointInfoOnMapPos.x,
          translateX: '10%',
          translateY: '10%'
        }
      }

      if (waypointInfoOnMapPos.x > screenRightLimit) {
        setMenuPosition('top-right')

        return {
          top: waypointInfoOnMapPos.y,
          left: waypointInfoOnMapPos.x,
          translateX: '-110%',
          translateY: '10%'
        }
      }

      setMenuPosition('center-top')

      return {
        top: waypointInfoOnMapPos.y,
        left: waypointInfoOnMapPos.x,
        translateX: '-50%',
        translateY: '10%'
      }
    }

    if (waypointInfoOnMapPos.x > screenRightLimit) {
      setMenuPosition('center-right')

      return {
        top: waypointInfoOnMapPos.y,
        left: waypointInfoOnMapPos.x,
        translateX: '-110%',
        translateY: '-50%'
      }
    }

    if (waypointInfoOnMapPos.x < screenLeftLimit) {
      setMenuPosition('center-left')

      return {
        top: waypointInfoOnMapPos.y,
        left: waypointInfoOnMapPos.x,
        translateX: '10%',
        translateY: '-50%'
      }
    }

    setMenuPosition('default')

    return {
      top: waypointInfoOnMapPos.y,
      left: waypointInfoOnMapPos.x,
      translateX: '10%',
      translateY: '-50%'
    }
  }, [waypointInfoOnMapPos])

  const handleCloseWaypointInfo = () => {
    clearTimeout(balloonTimeoutRef.current ?? undefined)
    setFocusedWaypoint(null)
    setWaypoint(null)
    setWaypointInfoOnMapPos({ x: 0, y: 0 })
  }

  const handleSymbolClick = (waypointId: string, point: Point) => {
    if (measuringSegmentState.active) return
    // TODO: Refactor race condition on feature click
    const ignoredLayers: string[] = []

    mapContext.map.getStyle().layers.forEach((layer) => {
      if (
        (ignoreMapClickLayers.includes(layer.id) || isAirspaceInteractiveLayer(layer.id)) &&
        !layer.id.includes('adhp')
      ) {
        ignoredLayers.push(layer.id)
      }
    })

    const features = mapContext.map.queryRenderedFeatures(point, { layers: ignoredLayers })
    if (features.length > 0) return

    setFocusedWaypoint(waypointId)

    const waypoint = adhps.find((w) => w.id.toValue().toString() === waypointId)

    let selectedWaypoint: Waypoint
    if (waypoint instanceof ADHP) {
      selectedWaypoint = waypoint
    } else {
      selectedWaypoint = userWaypointsRepository.getWaypoint(waypoint.id).getValue()
    }
    setWaypoint(selectedWaypoint)

    // Recentraliza o aeródromo na área visível útil (descontando painéis/drawer abertos), preservando
    // o zoom atual. O balão (WaypointInfo) é ancorado por posição de TELA, então só é reaberto após a
    // animação — reprojetando a posição final do ponto — para não ficar deslocado em relação ao símbolo.
    clearTimeout(balloonTimeoutRef.current ?? undefined)
    setWaypointInfoOnMapPos({ x: 0, y: 0 })

    const { coordinates } = selectedWaypoint
    const duration = 500
    MapImperativeInterface.flyToCoordinates(coordinates, mapContext.map.getZoom(), duration, getFeatureFlyToPadding())

    balloonTimeoutRef.current = setTimeout(() => {
      const projected = mapContext.map.project([coordinates.longitude, coordinates.latitude])
      setWaypointInfoOnMapPos({ x: projected.x, y: projected.y })
    }, duration)
  }

  return (
    <>
      {adhps && (
        <MapADHP
          adhps={adhps}
          minRunwayLength={mapADHPState.minRunwayLength ?? 0}
          showAerodromes={mapADHPState.aerodromes ?? false}
          showHelipads={mapADHPState.helipads ?? false}
          showUserWaypoints={mapADHPState.userWaypoints ?? false}
          disabledUserWaypointIds={waypointVisibility.disabledIds}
          onlyPaved={mapADHPState.pavedRunway ?? false}
          onlyNightOperation={mapADHPState.nightOperation ?? false}
          onlyATS={mapADHPState.ats ?? false}
          onlyIFR={mapADHPState.ifr ?? false}
          onSymbolClick={handleSymbolClick}
          focusedWaypoint={focusedWaypoint}
        />
      )}
      {focusedWaypoint && waypointInfoOnMapPos.x !== 0 && waypointInfoOnMapPos.y !== 0 && (
        <WaypointInfoContainer
          pointerPosition={menuPosition}
          menuScreenPos={waypointInfoMenuPosition}
          waypoint={waypoint}
          onOverlayClick={handleCloseWaypointInfo}
        />
      )}
    </>
  )
}

export { MapADHPPresenter }
